import React, { useState } from 'react';
import Spinner from '../../../components/react/spinner/spinner';
import { Navigate } from 'react-router';
import AlertPopup from '../../../components/react/alert_popup/alert_popup';
import styles from './verification_email_sent.module.scss';
import { MEMBERSHIPS_WITH_LANDING_PAGE } from '../../constants/membership_flow.constants';
import classNames from 'classnames';
import { PackageShortName } from '../../types/membership_flow.types';

interface Props {
  packageShortName: PackageShortName;
  emailRecipient: string;
  registerUserRequest: () => void;
  mailTo: 'enterprise@devex.com'|'registration@devex.com';
}

const VerificationEmailSent: React.FC<Props> = ({
  packageShortName,
  emailRecipient,
  registerUserRequest,
  mailTo,
}) => {
  const [alertData, setAlertData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const isPackageWithLandingPage = MEMBERSHIPS_WITH_LANDING_PAGE.includes(packageShortName);

  /**
   * Triggers a registration user request
   *
   * @returns {undefined}
   */
  const resendActivationEmail = async () => {
    setIsLoading(true);

    try {
      await registerUserRequest();
    } catch {
      setAlertData({
        type: 'error',
        message: 'Error re-sending user registration request, please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderTitle = (
    <h1
      className={classNames(
        styles.title,
        isPackageWithLandingPage
          ? `mb-3 fs-3 ${styles.embeddedRegistrationForm}`
          : 'mb-4 fs-2 fw-bold text-center'
      )}
    >
      Check your inbox
      {isPackageWithLandingPage && <span className='text-orange-devex'>.</span>}
    </h1>
  );

  const renderSubtitle = (
    <div className={`${styles.subtitle} text-gray text-start mb-3 pb-4`}>
      Click the link in the activation email we just sent to <b>{emailRecipient}</b> to complete
      your account set up. This link will expire in <b>2 hours</b>.
    </div>
  );

  const renderHelp = (
    <div className='help mt-4 text-start'>
      <b>Didn&apos;t receive an email?</b>
      <ul className='list-arrows mt-4 mb-sm-5 mb-0'>
        <li className='mb-2 text-gray-li'>Check your spam folder.</li>
        <li className='mb-2 text-gray-li'>
          Does your email address above look right? If not, restart your registration with the
          correct email address.
        </li>
      </ul>
    </div>
  );

  const renderContactLink = (
    <div className={styles.contact}>
      If you still have not received the email after resending, please{' '}
      <a href={`mailto:${mailTo}`} target='_blank' rel='noreferrer'>
        contact our support team
      </a>
      .
    </div>
  );

  if (!emailRecipient) {
    const redirectTo = window.location.pathname.replace('/verification-sent', '');
    return <Navigate push to={redirectTo} />;
  }

  return (
    <>
      <AlertPopup alertData={alertData} setAlertData={setAlertData} />
      <div data-testid='verification-email-sent' className={styles.verificationEmailSent}>
        {renderTitle}
        {renderSubtitle}
        {renderHelp}
        <button
          data-testid='resend-activation-email'
          type='button'
          className='btn btn-dark w-100 mt-3 mb-3'
          onClick={resendActivationEmail}
        >
          Resend activation email
        </button>
        {renderContactLink}
        <Spinner isLoading={isLoading} isAbsolute={true}></Spinner>
      </div>
    </>
  );
};

export default VerificationEmailSent;
